import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconHistory, IconArrowLeft, IconPencil, IconX } from '@tabler/icons-react';
import { GenericEntityUpsertDialog } from '../dialogs';
import { WorkflowStepEditDialog } from '../dialogs';
import { WorkflowDesigner } from '../workflows/WorkflowDesigner';
import { ReferencesGraph } from '../common/ReferencesGraph';
import { EntityDetailBindingsTab, EntityDetailSchemaTab } from '../dialogs/entityDetail';
import { EntityDetailJournalTab } from '../dialogs/entityDetail/EntityDetailJournalTab';
import { EntityDetailWorkflowStepsTab } from '../dialogs/entityDetail/EntityDetailWorkflowStepsTab';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { useViewModeSafe } from '../../contexts/ViewModeContext';
import { useEntityDetailLoader } from '../../hooks/useEntityDetailLoader';
import { useEntityMapTab, type UseEntityMapTabConfig } from '../../hooks/useEntityMapTab';
import { hasAnyWritableAttribute, type EntityEntitlementForTargetResponse } from '../../types/EntityEntitlements';
import { needsAttributeFetch } from '../../utils/entityPropertyUtils';
import { isContent, isEmailTemplate, isPhoto, isPerson, isWorkflow } from '../../utils/entityTypeDetection';
import { getSingularLabel } from '../../utils/pluralizationUtils';
import { ShadcnPersonHeroCard } from '../users/ShadcnPersonHeroCard';
import { resolveEntityViewHash } from '../../utils/entityViewNavigationUtils';
import { ShadcnContentBreadcrumb } from '../common/ShadcnContentBreadcrumb';
import { getEntityConfig } from '../../registry';

interface InlineEditorUnsavedState {
    active: boolean;
    hasUnsavedChanges: boolean;
    mode: 'create' | 'edit';
    entityType: string;
}

const EMPTY_INLINE_EDITOR_UNSAVED_STATE: InlineEditorUnsavedState = {
    active: false,
    hasUnsavedChanges: false,
    mode: 'edit',
    entityType: 'entity'
};

export interface UnifiedEntityScreenProps {
    open: boolean;
    entityType: string;
    entityId: string;
    mode: 'view' | 'edit';
    existingEntity?: any;
    targetEntitlement?: EntityEntitlementForTargetResponse | null;
    onClose: () => void;
    onModeChange: (mode: 'view' | 'edit') => void;
    onSuccess?: (savedEntity: any) => void;
    onUnsavedStateChange?: (state: InlineEditorUnsavedState) => void;
}

export const UnifiedEntityScreen: React.FC<UnifiedEntityScreenProps> = ({
    open,
    entityType,
    entityId,
    mode,
    existingEntity,
    targetEntitlement,
    onClose,
    onModeChange,
    onSuccess,
    onUnsavedStateChange,
}) => {
    const { entityTypes } = useEntityTypes();
    const { isStandard } = useViewModeSafe();
    const [inlineEditorUnsavedState, setInlineEditorUnsavedState] = useState<InlineEditorUnsavedState>(EMPTY_INLINE_EDITOR_UNSAVED_STATE);
    const entityConfig = getEntityConfig(entityType);
    const editorType = entityConfig?.editor ?? 'generic';
    const isPhotoEntity = isPhoto(entityType);
    const stableEntity = useMemo(() => ({ Id: entityId }), [entityId]);
    const {
        fullEntity,
        schema,
        photo,
        targetEntitlement: loadedTargetEntitlement,
        loadNonIndexedAttribute,
    } = useEntityDetailLoader({
        open,
        entity: stableEntity,
        entityType,
        selectedEntityId: entityId,
        includeContentBody: isContent(entityType) || isEmailTemplate(entityType),
    });

    const displayEntity = fullEntity ?? existingEntity ?? null;
    const effectiveTargetEntitlement = targetEntitlement ?? loadedTargetEntitlement;
    const isSchemaAttribute = entityType.trim().toLowerCase() === 'schemaattribute';
    const showJournalTab = entityType.trim().toLowerCase() !== 'journalentry';
    const canSwitchToEdit = mode === 'view'
        && (effectiveTargetEntitlement == null || hasAnyWritableAttribute(effectiveTargetEntitlement));
    const canSwitchToView = mode === 'edit' && !inlineEditorUnsavedState.hasUnsavedChanges;

    const handleUnsavedStateChange = useCallback((state: InlineEditorUnsavedState) => {
        setInlineEditorUnsavedState(state.active ? state : EMPTY_INLINE_EDITOR_UNSAVED_STATE);
        onUnsavedStateChange?.(state.active ? state : EMPTY_INLINE_EDITOR_UNSAVED_STATE);
    }, [onUnsavedStateChange]);

    const mapTabConfig = useMemo((): UseEntityMapTabConfig => {
        const normalizedType = entityType.trim().toLowerCase();
        if (['studenttransportneed', 'studenttransportationneed'].includes(normalizedType)) {
            return {
                mode: 'addressFields',
                addressFields: [
                    { fieldName: 'DropOffAddress', label: 'Drop-off' },
                    { fieldName: 'PickupAddress', label: 'Pickup' }
                ],
                tabLabel: 'Maps',
                supportsMapping: (value: string) => ['studenttransportneed', 'studenttransportationneed'].includes((value ?? '').trim().toLowerCase())
            };
        }

        if (['school', 'building', 'responsibilitycenter', 'studentbusstop'].includes(normalizedType)) {
            return {
                mode: 'locality',
                tabLabel: 'Map',
                supportsMapping: (value: string) => ['school', 'building', 'responsibilitycenter', 'studentbusstop'].includes((value ?? '').trim().toLowerCase()),
                hasLocationData: (entity: any) => !!entity && (
                    !!(entity.Address || entity.City || entity.State || entity.Zip) ||
                    !!(entity.Latitude && entity.Longitude)
                )
            };
        }

        return {
            mode: 'locality',
            tabLabel: 'Map',
            supportsMapping: () => false,
            hasLocationData: () => false
        };
    }, [entityType]);

    const detailTabsBeforeMapCount = 2 + (isWorkflow(entityType) ? 1 : 0) + (isSchemaAttribute ? 1 : 0);
    const mapTabResult = useEntityMapTab({
        entity: displayEntity,
        entityType,
        config: mapTabConfig,
        additionalTabsStartIndex: 3 + detailTabsBeforeMapCount
    });

    const handleContentLinkClick = useCallback(async (targetEntityId: string, _displayName: string) => {
        const hash = await resolveEntityViewHash('content', targetEntityId);
        // Use direct hash assignment instead of pushHashRoute to force the app router
        // to fully re-process the route, even when staying within the same entity type.
        window.location.hash = hash.startsWith('#') ? hash.substring(1) : hash;
    }, []);

    const supplementalTabs = useMemo(() => {
        const tabs: Array<{
            key?: string;
            label: React.ReactNode;
            content: React.ReactNode;
            leftSection?: React.ReactNode;
            onActivate?: () => void;
        }> = [
            {
                key: 'references',
                label: 'References',
                content: displayEntity
                    ? <ReferencesGraph entity={displayEntity} />
                    : <Text size="sm" c="dimmed">Entity details are still loading.</Text>
            },
            {
                key: 'schema',
                label: 'Schema',
                content: (
                    <EntityDetailSchemaTab
                        schemaAttributes={schema.schemaAttributes}
                        schemaLoading={schema.schemaLoading}
                        entityType={entityType}
                        entityTypes={entityTypes}
                        authResolved={true}
                    />
                )
            }
        ];

        if (isWorkflow(entityType)) {
            tabs.push({
                key: 'steps',
                label: 'Steps',
                content: <EntityDetailWorkflowStepsTab workflow={fullEntity ?? null} />
            });
        }

        if (isSchemaAttribute) {
            tabs.push({
                key: 'bindings',
                label: 'Bindings',
                content: <EntityDetailBindingsTab bindings={[]} bindingsLoading={false} bindingsError={null} />
            });
        }

        mapTabResult.additionalTabs.forEach((tab) => {
            tabs.push({
                key: `map-${tab.value}`,
                label: tab.label,
                content: tab.content,
                onActivate: () => mapTabResult.handleTabChange(tab.value)
            });
        });

        if (showJournalTab && displayEntity && (displayEntity.Id || displayEntity.id)) {
            tabs.push({
                key: 'journal',
                label: 'Journal',
                leftSection: <IconHistory size={16} />,
                content: (
                    <EntityDetailJournalTab
                        entityType={entityType}
                        targetId={String(displayEntity.Id || displayEntity.id)}
                        currentEntity={displayEntity}
                    />
                )
            });
        }

        if (isStandard) {
            return tabs.filter(t => t.key === 'journal');
        }
        return tabs;
    }, [
        displayEntity,
        entityType,
        entityTypes,
        fullEntity,
        isSchemaAttribute,
        isStandard,
        mapTabResult,
        schema.schemaAttributes,
        schema.schemaLoading,
        showJournalTab
    ]);

    // Auto-load Body when opening Content/EmailTemplate (both view and edit)
    const bodyAutoLoadedRef = React.useRef<string | false>(false);
    useEffect(() => {
        if (open && displayEntity && (isContent(entityType) || isEmailTemplate(entityType))
            && needsAttributeFetch(displayEntity, 'Body')) {
            const currentId = displayEntity.Id || displayEntity.id || entityId;
            if (bodyAutoLoadedRef.current !== currentId) {
                bodyAutoLoadedRef.current = currentId;
                void loadNonIndexedAttribute('Body');
            }
        }
        if (!open) bodyAutoLoadedRef.current = false;
    }, [mode, open, displayEntity, entityType, entityId, loadNonIndexedAttribute]);

    // Auto-load Data when opening a Photo entity (binary payload excluded from list fetches)
    const dataAutoLoadedRef = React.useRef<string | false>(false);
    useEffect(() => {
        if (open && displayEntity && isPhotoEntity && needsAttributeFetch(displayEntity, 'Data')) {
            const currentId = displayEntity.Id || displayEntity.id || entityId;
            if (dataAutoLoadedRef.current !== currentId) {
                dataAutoLoadedRef.current = currentId;
                void loadNonIndexedAttribute('Data');
            }
        }
        if (!open) dataAutoLoadedRef.current = false;
    }, [open, displayEntity, isPhotoEntity, entityId, loadNonIndexedAttribute]);

    const handleModeChange = useCallback(async (nextMode: 'view' | 'edit') => {
        if (nextMode === 'edit' && displayEntity && (isContent(entityType) || isEmailTemplate(entityType)) && needsAttributeFetch(displayEntity, 'Body')) {
            try {
                await loadNonIndexedAttribute('Body');
            } catch {
                // The inline editor still has its own edit-load fallback.
            }
        }
        onModeChange(nextMode);
    }, [displayEntity, entityType, loadNonIndexedAttribute, onModeChange]);

    if (!open || !entityId) {
        return null;
    }

    const entityLabel = getSingularLabel(entityType);
    const isContentType = isContent(entityType);
    const currentDisplayName = displayEntity?.DisplayName || displayEntity?.displayName || entityId;

    return (
        <Box mt="md" pb="lg">
            {isContentType && (
                <ShadcnContentBreadcrumb
                    currentEntityId={entityId}
                    currentDisplayName={currentDisplayName}
                />
            )}
            <Group justify="space-between" align="center" mb="md" role="toolbar" aria-label="Entity navigation">
                <Group gap="sm" align="center">
                    <Button
                        variant="default"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={onClose}
                    >
                        Back to List
                    </Button>
                    <Title order={4} m={0}>
                        {mode === 'edit' ? `Edit ${entityLabel}` : `View ${entityLabel}`}
                    </Title>
                </Group>
                <Group gap="sm">
                    {mode === 'view' ? (
                        <Button
                            variant="filled"
                            leftSection={<IconPencil size={16} />}
                            onClick={() => { void handleModeChange('edit'); }}
                            disabled={!canSwitchToEdit}
                        >
                            Edit
                        </Button>
                    ) : (
                        canSwitchToView && (
                            <Button
                                variant="default"
                                leftSection={<IconX size={16} />}
                                onClick={() => { void handleModeChange('view'); }}
                            >
                                Cancel
                            </Button>
                        )
                    )}
                </Group>
            </Group>

            {isPerson(entityType) && displayEntity && (
                <ShadcnPersonHeroCard
                    entity={displayEntity}
                    entityType={entityType}
                    mode={mode}
                    photo={photo}
                />
            )}

            {isPhotoEntity && mode === 'view' && (
                <PhotoPreview
                    photoData={photo.photoData}
                    photoLoading={photo.photoLoading}
                    contentType={displayEntity?.ContentType}
                    expanded={photo.photoExpanded}
                    onToggleExpand={() => photo.setPhotoExpanded(!photo.photoExpanded)}
                />
            )}

            {mode === 'edit' && editorType === 'workflowDesigner' ? (
                <WorkflowDesigner
                    open={open}
                    onClose={() => { void handleModeChange('view'); }}
                    onSuccess={(saved) => {
                        onSuccess?.(saved);
                        void handleModeChange('view');
                    }}
                    workflow={fullEntity ?? undefined}
                    entityType={entityType}
                    presentation="inline"
                />
            ) : mode === 'edit' && editorType === 'workflowStep' ? (
                <WorkflowStepEditDialog
                    open={open}
                    onClose={() => { void handleModeChange('view'); }}
                    onSuccess={(saved) => {
                        onSuccess?.(saved);
                        void handleModeChange('view');
                    }}
                    step={fullEntity ?? undefined}
                    entityType="WorkflowActivity"
                    presentation="inline"
                />
            ) : (
                <GenericEntityUpsertDialog
                    open={open}
                    onClose={onClose}
                    onSuccess={onSuccess}
                    entityType={entityType}
                    entityId={entityId}
                    existingEntity={fullEntity ?? undefined}
                    targetEntitlement={effectiveTargetEntitlement}
                    parentProvidesEntityLoad
                    presentation="inline"
                    surfaceMode={mode}
                    onSurfaceModeChange={handleModeChange}
                    inlineNavigationOwner="parent"
                    supplementalTabs={supplementalTabs}
                    onContentLinkClick={isContent(entityType) ? handleContentLinkClick : undefined}
                    onUnsavedStateChange={handleUnsavedStateChange}
                />
            )}
        </Box>
    );
};

function PhotoPreview({
    photoData,
    photoLoading,
    contentType,
    expanded,
    onToggleExpand,
}: {
    photoData: string | null;
    photoLoading: boolean;
    contentType?: string;
    expanded: boolean;
    onToggleExpand: () => void;
}) {
    if (!photoData && !photoLoading) return null;

    const mime = (contentType || 'image/jpeg').toString().trim();

    return (
        <Paper withBorder p="md" mb="md" radius="md" ta="center">
            {photoLoading ? (
                <Group justify="center" p="md">
                    <Loader size={48} />
                </Group>
            ) : photoData ? (
                <Stack align="center" gap="sm">
                    <img
                        src={`data:${mime};base64,${photoData}`}
                        alt="Photo preview"
                        onClick={onToggleExpand}
                        style={{
                            maxWidth: expanded ? '100%' : 240,
                            maxHeight: expanded ? '70vh' : 240,
                            borderRadius: 8,
                            objectFit: 'contain',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease-in-out',
                            boxShadow: expanded ? '0 4px 20px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.15)',
                        }}
                    />
                    <Text size="xs" c="dimmed">
                        {expanded ? 'Click to shrink' : 'Click to enlarge'}
                    </Text>
                </Stack>
            ) : null}
        </Paper>
    );
}
